import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Plus, BookOpen, Brain, Settings, LogOut, Menu } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/capture', label: 'New Capture', icon: Plus },
  { path: '/library', label: 'SOP Library', icon: BookOpen },
  { path: '/brain', label: 'Ask the Brain', icon: Brain },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-[#080812] flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#111827] border-r border-[#374151] transform transition-transform duration-200 lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#374151]">
            <img src="/distill-logo.png" alt="Distill" className="h-16" data-testid="app-logo" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${isActive ? 'bg-[#AAFF00] text-[#080812]' : 'text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#1F2937]'}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon size={18} strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#374151]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-xs font-medium">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#F9FAFB] truncate">{user?.full_name || 'User'}</p>
                <p className="text-xs text-[#9CA3AF] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#1F2937] rounded-md transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-[#111827] border-b border-[#374151]">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={24} className="text-[#F9FAFB]" />
          </button>
          <img src="/distill-logo.png" alt="Distill" className="h-12" />
          <div className="w-6" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
